import type { Product } from '@/api/products';
import { getDecorationCategoryFromCode } from '@/lib/decoration-method';
import type {
  GuidedPreselection,
  GuidedProcedure,
} from '@/stores/guidedEntryStore';

const MOTIF_POSITIONS = new Set(['front', 'back']);

/**
 * Whether a product can be decorated by the given guided procedure.
 *
 * Only the front and back positions count: those are the ones step 1 places
 * motifs on, so a print on e.g. the inside neck label must not make an
 * embroidery-only product look printable.
 *
 * Reads every method those positions offer — a position printed by default can
 * still be embroidered on request — and ignores methods an admin switched off.
 * Returns null when the product carries no method information at all: unknown
 * is not a mismatch, so no warning is raised for it.
 */
export function productSupportsProcedure(
  product: Pick<Product, 'positions'>,
  procedure: GuidedProcedure
): boolean | null {
  const offered = new Set<GuidedProcedure>();

  for (const position of product.positions ?? []) {
    if (!MOTIF_POSITIONS.has(position.slug)) continue;
    const methods = position.availableDecorationMethods ?? [];
    const codes =
      methods.length > 0
        ? methods.filter((m) => m.isActive !== false).map((m) => m.code)
        : [position.decorationMethods?.code];

    for (const code of codes) {
      const category = getDecorationCategoryFromCode(code);
      if (category) offered.add(category);
    }
  }

  if (offered.size === 0) return null;
  return offered.has(procedure);
}

/** The procedure a product is left with when it does not support `procedure`. */
export function otherProcedure(procedure: GuidedProcedure): GuidedProcedure {
  return procedure === 'print' ? 'embroidery' : 'print';
}

const PROCEDURES: readonly GuidedProcedure[] = ['print', 'embroidery'];

function isProcedure(value: string | null): value is GuidedProcedure {
  return (PROCEDURES as readonly (string | null)[]).includes(value);
}

export interface StartProcedureInput {
  /** Raw `?procedure=`; anything but `print` or `embroidery` is ignored. */
  procedureParam: string | null;
  /** Whether the URL names a product (`?product=`, `?SKU=`, `?sku=`). */
  namesProduct: boolean;
  /** State of the catalogue query. */
  catalog: 'loading' | 'error' | 'ready';
  products: Pick<Product, 'id' | 'positions'>[] | undefined;
  /** What CreatorPage preselected; null until it has decided. */
  preselection: GuidedPreselection | null;
}

/**
 * A procedure, `wait` while the preselection is still being resolved, or
 * `none` when no start can be given and the seller chooses from the hero.
 */
export type StartProcedure = GuidedProcedure | 'wait' | 'none';

/**
 * The procedure an embedded guided entry starts on (issue #367).
 *
 * An explicit `?procedure=` wins and never waits. Without a product in the
 * URL the start is Druck at once, as before. With one, the start waits for
 * CreatorPage's preselection and follows the product: Stick only for a
 * product that offers Stick and not Druck on its motif positions, Druck in
 * every other case, unknown method data included.
 */
export function resolveStartProcedure(
  input: StartProcedureInput
): StartProcedure {
  if (isProcedure(input.procedureParam)) return input.procedureParam;
  if (!input.namesProduct) return 'print';
  if (input.catalog === 'error') return 'none';
  // resolveInitialSelection keeps waiting on an empty catalogue, so the
  // preselection would never arrive.
  if (input.catalog === 'ready' && input.products?.length === 0) {
    return 'print';
  }

  const { preselection } = input;
  if (preselection === null) return 'wait';
  if (preselection.productId === null) return 'print';
  // The product's methods are needed to decide; this component's own
  // catalogue query may still be on its way.
  if (input.catalog !== 'ready') return 'wait';

  const product = input.products?.find((p) => p.id === preselection.productId);
  if (!product) return 'print';

  const embroideryOnly =
    productSupportsProcedure(product, 'print') === false &&
    productSupportsProcedure(product, 'embroidery') === true;
  return embroideryOnly ? 'embroidery' : 'print';
}
